import { faCircle, faCircleCheck, faCircleInfo, faCirclePause, faCirclePlay, faForward } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import Dropdown from "react-bootstrap/Dropdown";
import ReactDOM from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import clone from 'rfdc/default';
import { ButtonAtom, CheckboxAtom, DatePickerAtom, DropdownAtom, ModalAtom, TextAreaAtom, TextFieldAtom } from "../../../components/atoms/atom";
import { AuthContext } from '../../../contexts/AuthProvider';
import { setting } from '../../../store/services/index';
import { HELP_MODES } from "../../../utils/constant";
import { formatValueToLocale, getFormattedDate, showError } from "../../../utils/method";
import { statusOptions, STEP_STATUS } from './constant';
import './detail.scss';

function DetailModal(props) {
	const { show, onClose, onSubmit, attributeGroups, selectedOrders, onAddProcessStepClick, orderType = '', onClickInfo, socketId, updateLocalProcessStep, onOrderUpdated } = props;
	const clonedAttributeGroup = clone(attributeGroups);
	const { t } = useTranslation();
	const { auth, setAuth, helpMode } = useContext(AuthContext);
	const [updatedInfo, setUpdatedInfo] = useState([]);
	const [jumpModal, setJumpModal] = useState({ show: false, step: null });
	const [isEditMode, setIsEditMode] = useState(false);
	const [changelogModal, setChangelogModal] = useState({ show: false, data: [] });
	const [changelogLoading, setChangelogLoading] = useState(false);
	const [editSaving, setEditSaving] = useState(false);

	const getUserInfo = async () => {
		let res = '';
		let role = auth?.role;
		if (!role) {
			res = await setting.getUserInfo();
			role = res.data.role;
			setAuth({ ...auth, role: res.data.role, name: res.data.name })
		}
		initializeDynamicValue();
	};

	const mapStatusOptions = (statusNoArray) => {
		const mappedStatus = statusNoArray.map((val) => {
			return statusOptions.find((st) => st.status === val) || {};
		});
		return mappedStatus;
	}

	const getStatusOptionsInfo = () => {
		const existProcessSteps = clone(selectedOrders[0].orderProcessSteps);
		const isStartedPause = existProcessSteps.find((step) => step.status === 3 || step.status === 4);
		let resultObj = {};
		let options = [];
		let optionsVisibleStepId = '';

		if (isStartedPause) {
			options = isStartedPause.status === 3 ? mapStatusOptions([2, 4, 5]) : mapStatusOptions([2, 3, 5]);
			optionsVisibleStepId = isStartedPause?.id || '';

		} else {
			const lastDoneAvailabelStep = clone(existProcessSteps).reverse().find((step) => step.status === 2 || step.status === 5);
			if (lastDoneAvailabelStep?.status === 2) {
				options = mapStatusOptions([3, 4, 5]);
				const visibleIndex = existProcessSteps.findIndex((step) => step.id === lastDoneAvailabelStep.id);
				if (visibleIndex > 0) {
					resultObj.secondaryOptions = mapStatusOptions([2, 3, 4]);
					resultObj.secondaryOptionsVisbleStepId = existProcessSteps[visibleIndex - 1].id;
				}
			}
			if (lastDoneAvailabelStep?.status === 5)
				options = mapStatusOptions([2, 3, 4]);

			optionsVisibleStepId = lastDoneAvailabelStep?.id || '';
		}
		return { ...resultObj, options, optionsVisibleStepId }
	}

	// Check if a step can be jumped to (forward step after current active step)
	const isForwardJumpableStep = (orderStep) => {
		const existProcessSteps = clone(selectedOrders[0].orderProcessSteps);
		const currentStepPosition = orderStep.processStepPosition;

		// Find the current active step (IN_PROGRESS or PAUSE) or last AVAILABLE/DONE step
		const activeStep = existProcessSteps.find((step) => step.status === 3 || step.status === 4);
		const lastAvailableStep = clone(existProcessSteps).reverse().find((step) => step.status === 2 || step.status === 5);

		let activePosition = -1;
		if (activeStep) {
			activePosition = activeStep.processStepPosition;
		} else if (lastAvailableStep) {
			activePosition = lastAvailableStep.processStepPosition;
		}

		// Step is jumpable if it's after the current active step and not already DONE
		return currentStepPosition > activePosition && orderStep.status !== 5;
	};

	// Handle forward jump to a step
	const handleForwardJump = async (targetStatus) => {
		if (!jumpModal.step) return;

		try {
			const res = await setting.forwardJumpToStep(selectedOrders[0]?.id, {
				orderProcessStepId: jumpModal.step.id,
				status: targetStatus,
				resourceId: jumpModal.step.resource.id,
				processStepId: jumpModal.step.processStep.id,
				processStepPosition: jumpModal.step.processStepPosition,
				socketId: socketId,
			});
			updateLocalProcessStep(res.data);
			setJumpModal({ show: false, step: null });
		} catch (error) {
			showError(t, error);
		}
	};

	// Show forward jump icon for steps that can be jumped to
	const showForwardJumpIcon = (orderStep) => {
		if (auth.role === 'SALES') return null;
		if (!isForwardJumpableStep(orderStep)) return null;

		return (
			<FontAwesomeIcon
				icon={faForward}
				className="icon-forward-jump ms-2"
				role="button"
				title={t('forward-jump-tooltip')}
				onClick={() => setJumpModal({ show: true, step: orderStep })}
			/>
		);
	};

	// Jump modal component - using portal to render outside detail modal
	const renderJumpModal = () => {
		if (!jumpModal.show) return null;

		return ReactDOM.createPortal(
			<div className="jump-modal-wrapper">
				<div className="jump-modal-backdrop" onClick={() => setJumpModal({ show: false, step: null })} />
				<div className="jump-modal-content">
					<div className="jump-modal-header">
						<h5>{t('forward-jump-title')}</h5>
						<button type="button" className="btn-close" onClick={() => setJumpModal({ show: false, step: null })} />
					</div>
					<div className="jump-modal-body">
						<p>{t('forward-jump-message', { stepName: jumpModal.step?.processStep?.label || '' })}</p>
						<p className="text-muted small">{t('forward-jump-intermediate-info')}</p>
					</div>
					<div className="jump-modal-footer">
						<ButtonAtom className="btn btn-secondary" onClick={() => setJumpModal({ show: false, step: null })}>
							{t('cancel')}
						</ButtonAtom>
						<ButtonAtom className="btn btn-success" onClick={() => handleForwardJump(STEP_STATUS[3])}>
							<FontAwesomeIcon icon={faCirclePlay} className="me-2" />
							{t('start')}
						</ButtonAtom>
						<ButtonAtom className="btn btn-warning" onClick={() => handleForwardJump(STEP_STATUS[4])}>
							<FontAwesomeIcon icon={faCirclePause} className="me-2" />
							{t('pause')}
						</ButtonAtom>
						<ButtonAtom className="btn btn-primary" onClick={() => handleForwardJump(STEP_STATUS[5])}>
							<FontAwesomeIcon icon={faCircleCheck} className="me-2" />
							{t('done')}
						</ButtonAtom>
					</div>
				</div>
			</div>,
			document.body
		);
	};

	// Fetch changelog for the order
	const fetchChangelog = async () => {
		if (selectedOrders.length !== 1) return;
		setChangelogLoading(true);
		try {
			const res = await setting.getOrderChangelog(selectedOrders[0]?.id);
			setChangelogModal({ show: true, data: res.data || [] });
		} catch (error) {
			showError(t, error);
		} finally {
			setChangelogLoading(false);
		}
	};

	// Save order with audit trail (for release/implement mode)
	const handleSaveWithAudit = async () => {
		if (selectedOrders.length !== 1) return;

		const changedFields = updatedInfo.filter(info => info.isUpdated);
		if (changedFields.length === 0) {
			setIsEditMode(false);
			return;
		}

		// Prepare payload - same format as planning mode, backend handles audit trail
		const orderInfo = changedFields.map(field => {
			let updatedValue = '';

			// Format new value (same as onOrderUpdate in open-order.jsx)
			if (field.attributeFormat === 'DATE') {
				updatedValue = getFormattedDate(field.value);
			} else if (field.attributeFormat === 'DATETIME') {
				updatedValue = getFormattedDate(field.value, true);
			} else {
				updatedValue = field.value != null ? field.value.toString() : '';
			}

			return {
				id: field.id,
				value: updatedValue,
			};
		});

		setEditSaving(true);
		try {
			const response = await setting.updateOrder({
				orders: [selectedOrders[0]?.id],
				orderInfo: orderInfo,
				mode: 'OPERATOR'
			});
			setIsEditMode(false);
			// Notify parent to refresh data
			if (onOrderUpdated) {
				onOrderUpdated(response?.data || []);
			}
		} catch (error) {
			showError(t, error);
		} finally {
			setEditSaving(false);
		}
	};

	// Cancel edit mode
	const handleCancelEdit = () => {
		setIsEditMode(false);
		initializeDynamicValue();
	};

	// Format changes array for display
	const formatChanges = (changes) => {
		if (!changes || changes.length === 0) return '';
		return changes.map(c => `${c.attributeLabel}: ${c.oldValue || '-'} → ${c.newValue || '-'}`).join('\n');
	};

	// Changelog modal component - using portal to render outside detail modal
	const renderChangelogModal = () => {
		if (!changelogModal.show) return null;

		return ReactDOM.createPortal(
			<div className="changelog-modal-wrapper">
				<div className="changelog-modal-backdrop" onClick={() => setChangelogModal({ show: false, data: [] })} />
				<div className="changelog-modal-content">
					<div className="changelog-modal-header">
						<h5>{t('changelog-title')}</h5>
						<button type="button" className="btn-close" onClick={() => setChangelogModal({ show: false, data: [] })} />
					</div>
					<div className="changelog-modal-body">
						{changelogModal.data.length > 0 ? (
							<div className="changelog-table-container">
								<table className="table table-striped table-hover">
									<thead>
										<tr>
											<th style={{ width: '160px' }}>{t('changelog-date')}</th>
											<th style={{ width: '140px' }}>{t('changelog-changed-by')}</th>
											<th>{t('changelog-changes')}</th>
										</tr>
									</thead>
									<tbody>
										{changelogModal.data.map((entry, idx) => (
											<tr key={idx}>
												<td>{getFormattedDate(entry.createdAt, true)}</td>
												<td>{entry.changedBy}</td>
												<td>
													<ul className="changelog-changes-list mb-0">
														{entry.changes.map((change, cIdx) => (
															<li key={cIdx}>
																<strong>{change.attributeLabel}:</strong>{' '}
																<span className="text-danger">{change.oldValue || '-'}</span>
																{' → '}
																<span className="text-success">{change.newValue || '-'}</span>
															</li>
														))}
													</ul>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<p className="text-muted text-center">{t('changelog-no-data')}</p>
						)}
					</div>
				</div>
			</div>,
			document.body
		);
	};

	const isVisible = (fieldName) => {
		if (selectedOrders.length === 1) {
			return true;
		}
		if (selectedOrders.length > 1) {
			if (selectedOrders[0]?.missingValues?.length) {
				if (selectedOrders[0].missingValues.includes(fieldName)) {
					return true;
				}
				return false;
			} else {
				const isSame = selectedOrders.every((ord) => {
					return ord.orderInfo[fieldName].value === selectedOrders[0].orderInfo[fieldName].value;
				});
				return isSame;
			}
		}
	};

	const initializeDynamicValue = () => {
		const initialArray = [];
		clonedAttributeGroup.forEach((attributeGroup) => {
			attributeGroup.attributes.forEach((attribute) => {
				if (attribute.isChangeableAV && ['AV', 'ADMIN', 'SUPER_ADMIN'].includes(auth?.role) && isVisible(attribute.id)) {
					if (['DATE', 'DATETIME'].includes(attribute?.attributeFormat?.name)) {
						const val = selectedOrders[0]?.orderInfo[attribute.id]?.value;
						initialArray.push({
							isUpdated: false,
							id: attribute.id,
							value: new Date(val),
							attributeFormat: attribute?.attributeFormat?.name
						});

					} else if (attribute?.attributeFormat?.name === 'CHECKBOX') {
						const val = selectedOrders[0]?.orderInfo[attribute.id]?.value;
						initialArray.push({
							isUpdated: false,
							id: attribute.id,
							value: !!+val,
							attributeFormat: attribute?.attributeFormat?.name
						});

					} else {
						initialArray.push({
							isUpdated: false,
							id: attribute.id,
							value: selectedOrders[0]?.orderInfo[attribute.id].value,
							attributeFormat: attribute?.attributeFormat?.name
						});
					}
				}
			});
		});
		setUpdatedInfo(initialArray);
	};

	useEffect(() => {
		getUserInfo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOrders]);

	const onSubmitClick = () => {
		onSubmit(updatedInfo);
	}

	const getValue = (fieldId) => {
		const selectedField = updatedInfo.find((row) => {
			return row?.id === fieldId;
		});
		return selectedField?.value;
	}

	const updateValue = (fieldId, fieldValue) => {
		const fieldIndex = updatedInfo.findIndex((row) => {
			return row?.id === fieldId;
		});
		const localUpdatedInfo = [...updatedInfo];
		localUpdatedInfo[fieldIndex] = { ...localUpdatedInfo[fieldIndex], value: fieldValue, isUpdated: true };
		setUpdatedInfo(localUpdatedInfo);
	}

	const getEditableField = (attr) => {
		const value = getValue(attr.id);
		const additionalClass = !value && attr?.isRequired ? 'error' : '';
		const attributeType = attr?.attributeType?.name;
		const attributeFormat = attr?.attributeFormat?.name;
		if (attributeType === 'INPUT') {
			if (attributeFormat === 'DATE') {
				return <div className='body-inner'>
					<span className={additionalClass}>{attr.label}</span><span>
						<DatePickerAtom
							className={`form-control ${additionalClass}`}
							value={value}
							onChange={(e) => {
								const value = dayjs(e).toDate();
								updateValue(attr.id, value);
							}} /></span>
				</div>


			}
			if (attributeFormat === 'TEXT' || attributeFormat === 'NUMBER') {
				return <div className='body-inner'>
					<span className={additionalClass}>{attr.label}</span><span>
						<TextFieldAtom
							className={`form-control ${additionalClass}`}
							value={value}
							type={attributeFormat === 'NUMBER' ? 'number' : 'text'}
							onChange={(e) => {
								updateValue(attr.id, e.target.value);
							}} /></span>
				</div>
			}
			if (attributeFormat === "TEXTAREA") {
				return <div className='body-inner'>
					<span className={additionalClass}>{attr.label}</span><span>
						<TextAreaAtom
							className={`form-control ${additionalClass}`}
							value={value}
							onChange={(e) => {
								updateValue(attr.id, e.target.value);
							}} /></span>
				</div>
			}
			if (attributeFormat === 'DATETIME') {
				return <div className='body-inner'>
					<span className={additionalClass}>{attr.label}</span><span>
						<DatePickerAtom
							className={`form-control ${additionalClass}`}
							todayButton={t('today')}
							value={value}
							onChange={(e) => {
								const value = dayjs(e).toDate();
								updateValue(attr.id, value);
							}}
							dateFormat="dd.MM.yyyy h:mm aa"
							timeInputLabel={`${t('time')}:`}
							showTimeInput
						/></span>
				</div>
			}
			if (attributeFormat === "CHECKBOX") {
				return <div className='body-inner'>
					<span className={additionalClass}>{attr.label}</span><span>
						<CheckboxAtom
							// label={t('changeable_av')}
							checked={value}
							onChange={(evt) => updateValue(attr.id, evt.target.checked)} /></span>
				</div>
			}
		}
		if (attributeType === 'SELECT') {
			const options = attr?.attributeOptions?.split(',').map((opt) => {
				return {
					label: opt?.trim(),
					value: opt?.trim()
				}
			})
			return <div className='body-inner'>
				<span className={additionalClass}>{attr.label}</span><span>
					<DropdownAtom
						className={`form-control ${additionalClass}`}
						value={value}
						options={options}
						onChange={(e) => updateValue(attr.id, e.target.value)}
					/>
				</span>
			</div>
		}
	}

	const updateProcessStepStatus = async (info) => {
		try {
			const res = await setting.updateProcessStepStatus(selectedOrders[0]?.id, {
				orderProcessStepId: info.stepId,
				status: info.value,
				resourceId: info.resourceId,
				oldStatus: info.oldStatus,
				socketId: socketId,
				processStepId: info.processStepId,
			});
			updateLocalProcessStep(res.data);
		} catch (error) {
			showError(t, error);
		}
	}

	const getStatusDropDown = (dropDownInfo) => {
		const { options, stepId, resourceId, oldStatus, processStepId } = dropDownInfo;
		return <Dropdown className="status-dropdown">
			<Dropdown.Toggle disabled={auth.role === 'SALES'} variant="success" id="dropdown-basic" />
			<Dropdown.Menu>
				{options.map((opt) => <Dropdown.Item
					value={opt.value}
					onClick={((e) => updateProcessStepStatus({ value: opt.value, stepId, resourceId, oldStatus, processStepId }))}>{opt.label}</Dropdown.Item>)}
			</Dropdown.Menu>
		</Dropdown>
	};

	const showStatusDropDown = (orderStep) => {
		// In place of finding the resourceId and oldStatus from orderStep, get it from 'getStatusOptionsInfo()'.
		const stepId = orderStep.id;
		const resourceId = orderStep.resource.id;
		const processStepId = orderStep.processStep.id;
		const oldStatus = orderStep.status;
		const { options, optionsVisibleStepId, secondaryOptionsVisbleStepId = '', secondaryOptions = [] } = getStatusOptionsInfo();

		if (optionsVisibleStepId === stepId)
			return getStatusDropDown({ options, stepId: optionsVisibleStepId, resourceId, oldStatus, processStepId });

		if (secondaryOptionsVisbleStepId === stepId)
			return getStatusDropDown({ options: secondaryOptions, stepId: secondaryOptionsVisbleStepId, resourceId, oldStatus, processStepId });

		return '';

	};

	const processStepsContent = () => <>
		<div className='body-inner-title'>PROZESS&nbsp;
			{helpMode === HELP_MODES.ADVANCE && <OverlayTrigger overlay={<Tooltip>
				<Trans i18nKey="help-advance.order-detail-process">
					<FontAwesomeIcon icon={faCirclePlay} className="icon-play" /> Starten<br />
					<FontAwesomeIcon icon={faCirclePause} className="icon-pause" /> Pausieren<br />
					<FontAwesomeIcon icon={faCircleCheck} className="icon-done" /> Fertigmelden<br />
					<FontAwesomeIcon icon={faCircle} className="icon-not-started" /> zu vorherigem Prozessschritt<br />
					<FontAwesomeIcon icon={faForward} className="icon-forward-jump" /> Zum Schritt springen
				</Trans>
			</Tooltip>}>
				<FontAwesomeIcon icon={faCircleInfo} />
			</OverlayTrigger>}
		</div>
		<div className="modal-inner-wrapper process-step-list-wrapper">
			{
				selectedOrders[0].orderProcessSteps.map((orderStep) => {
					return <div className='body-inner'>
						<span className='process-content'>
							{(orderStep.status === 1 || orderStep.status === 2) && <FontAwesomeIcon icon={faCircle} className="icon-not-started" />}
							{orderStep.status === 3 && <FontAwesomeIcon icon={faCirclePlay} className="icon-play" />}
							{orderStep.status === 4 && <FontAwesomeIcon icon={faCirclePause} className="icon-pause" />}
							{orderStep.status === 5 && <FontAwesomeIcon icon={faCircleCheck} className="icon-done" />}
							{[3, 4].includes(selectedOrders[0].orderStatus) && !selectedOrders[0].isLocked ? showStatusDropDown(orderStep) : ''}
							{[3, 4].includes(selectedOrders[0].orderStatus) && !selectedOrders[0].isLocked ? showForwardJumpIcon(orderStep) : ''}
						</span>
						<span className='process-content'>
							<img src={orderStep?.resource?.icon} alt='' style={{ height: '24px' }} />
							<div className="title-container">
								<span className="title">{orderStep?.resource?.name}</span>
								<span className="subtitle">{orderStep?.resource?.locationName}</span>
							</div>
						</span>
						<span className='process-content'>{orderStep?.processStep?.label}</span>
					</div>
				})
			}
		</div>
		<div className="process-btn-container">
			{selectedOrders.length === 1 && <div>
				<FontAwesomeIcon icon={faCircleInfo} />
				<u className="ms-2 text-dark" role="button" onClick={onClickInfo}>Info</u>
			</div>}
			<ButtonAtom onClick={onAddProcessStepClick} disabled={auth.role === 'SALES'}>{t('order-detail-change-process')}</ButtonAtom>
		</div>
	</>;

	const getProcessSteps = () => {
		const count = selectedOrders?.length;
		if (count) {
			if (count === 1) {
				return processStepsContent();
			} else {
				const selectedProcessResourceId = selectedOrders[0].selectedProcessResourceId;
				const hasSimilarProcessSteps = selectedOrders.every((so) => JSON.stringify(so.selectedProcessResourceId) === JSON.stringify(selectedProcessResourceId));
				if (hasSimilarProcessSteps) {
					return processStepsContent();
				}
			}
		}
	};

	const getSortedAttributes = (attributeGroup) => {
		return attributeGroup.attributes?.sort(
			({ isRequired: isRequiredA = false }, { isRequired: isRequiredB = false }) => Number(isRequiredB) - Number(isRequiredA)
		);
	}

	const getModalTitle = () => {
		return `${t('detail')} (${selectedOrders.map((so) => so.salesOrderNumber).join(', ')})`;
	}

	const isAttributeEditable = (att) => att.isChangeableAV && ['AV', 'ADMIN', 'SUPER_ADMIN'].includes(auth?.role) && isVisible(att.id);

	// Check if edit mode can be enabled (release/implement only, single order, not finished/archived, and user has permission)
	const canEdit = () => {
		const orderStatus = selectedOrders[0]?.orderStatus;
		// Don't allow edit if order is FINISHED (4) or ARCHIVE (5)
		if (orderStatus === 4 || orderStatus === 5) {
			return false;
		}
		return ['release', 'implement'].includes(orderType) &&
			selectedOrders.length === 1 &&
			['AV', 'ADMIN', 'SUPER_ADMIN'].includes(auth?.role) &&
			clonedAttributeGroup.some(group => group.attributes.some(isAttributeEditable));
	};

	// Render header buttons for release/implement order types
	const renderHeaderButtons = () => {
		if (selectedOrders.length !== 1) {
			return null;
		}

		return (
			<>
				{['release', 'implement'].includes(orderType) && isEditMode ? (
					<>
						<ButtonAtom
							className="btn btn-outline-secondary mx-1"
							onClick={handleCancelEdit}
							disabled={editSaving}
						>
							{t('cancel')}
						</ButtonAtom>
						<ButtonAtom
							className="btn btn-primary mx-1 text-white"
							onClick={handleSaveWithAudit}
							disabled={editSaving}
						>
							{editSaving ? t('saving') : t('save')}
						</ButtonAtom>
					</>
				) : (
					<>
						{['release', 'implement'].includes(orderType) && canEdit() && (
							<ButtonAtom
								className="btn btn-outline-primary mx-1"
								onClick={() => setIsEditMode(true)}
							>
								{t('edit')}
							</ButtonAtom>
						)}
					</>
				)}
				<ButtonAtom
					className="btn btn-outline-info mx-1"
					onClick={fetchChangelog}
					disabled={changelogLoading}
				>
					{changelogLoading ? t('loading') : t('show-changelog')}
				</ButtonAtom>
			</>
		);
	};

	return (
		<>
			<ModalAtom
				title={getModalTitle()}
				customClassName='detail-modal'
				show={show}
				handleClose={onClose}
				handleSubmit={orderType === 'planning' ? onSubmitClick : onClose}
				hideCloseButton={orderType !== 'planning'}
				saveText={orderType !== 'planning' ? "close" : "save"}
				headerButtons={renderHeaderButtons()}
				body={<Container>
					<div className='modal-body-wrapper'>
						{clonedAttributeGroup?.map((attributeGroup) => {
							let showAttributeGroup = false;
							if (selectedOrders.length > 1) {
								showAttributeGroup = attributeGroup?.attributes?.findIndex(isAttributeEditable) > -1;
							} else {
								showAttributeGroup = true;
							}
							return showAttributeGroup && <div className='modal-body-content'>
								<div className="body-inner-title">{attributeGroup?.name}</div>
								<div className="modal-inner-wrapper">
									{getSortedAttributes(attributeGroup).map((attr) => {
										if ((orderType === 'planning' || (isEditMode && ['release', 'implement'].includes(orderType))) && isAttributeEditable(attr)) {
											return getEditableField(attr);
										}
										if (selectedOrders.length > 1) {
											return <></>;
										}
										let value = selectedOrders[0]?.orderInfo[attr.id].value;
										if (attr?.attributeType?.name === 'INPUT') {
											if (attr?.attributeFormat?.name === 'DATE') {
												value = getFormattedDate(value);
											}
											if (attr?.attributeFormat?.name === 'DATETIME') {
												value = getFormattedDate(value, true);
											}
											if (attr?.attributeFormat?.name === 'NUMBER') {
												if (attr.externalSystemIdentifier === 'WEIGHT_P') {
													value = formatValueToLocale(value, false, 3)
												} else {
													value = value || '';
												}
											}
										}
										return <div className='body-inner'><span>{attr.label}</span><span>{value}</span></div>
									})}
								</div>
							</div>
						})}
						<div className='modal-body-content'>
							{getProcessSteps()}
						</div>
					</div>
				</Container>}
			/>
			{renderJumpModal()}
			{renderChangelogModal()}
		</>
	);
}

export default DetailModal;
